import React, { useState, useEffect, useCallback, useRef } from 'react';
import EasyReports from './reports/EasyReports';
import {
  BarChart3,
  ChevronDown,
  Plus,
  Trash2,
  Play,
  Save,
  Download,
  X,
  AlertCircle,
  CheckCircle,
  Search,
  Filter,
  Settings,
  Link as LinkIcon,
  Eye,
  EyeOff,
  GripVertical,
  ArrowRight,
  ArrowLeft,
  Columns,
} from 'lucide-react';

const COLORS = {
  primary: '#2E8B98',
  teal: '#2E8B98',
  green: '#62BB46',
  orange: '#F26522',
  red: '#DC3545',
  dark: '#256F7A',
  light: '#F8F9FA',
  white: '#FFFFFF',
  border: '#e2e8f0',
  text: '#2d3748',
  textLight: '#718096',
  dropTarget: '#E0F4F8',
  dragActive: '#2E8B9820',
};

const OPERATORS = ['=', '!=', '>', '<', '>=', '<=', 'LIKE', 'IS NULL', 'IS NOT NULL'];

// ─── Drag Handle Icon ────────────────────────────────────────────────────────

function DragHandle({ size = 16 }) {
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      cursor: 'grab',
      color: COLORS.textLight,
      opacity: 0.6,
      flexShrink: 0,
    }}>
      <GripVertical size={size} />
    </span>
  );
}

// ─── Reusable Drag-Reorder List Hook (inline, lightweight) ─────────────────

function useDragList(items, onReorder) {
  const dragIdx = useRef(null);
  const overIdx = useRef(null);
  const [dragging, setDragging] = useState(-1);
  const [over, setOver] = useState(-1);

  const handlers = (index) => ({
    draggable: true,
    onDragStart: (e) => {
      dragIdx.current = index;
      setDragging(index);
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', String(index));
    },
    onDragOver: (e) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      if (overIdx.current !== index) {
        overIdx.current = index;
        setOver(index);
      }
    },
    onDragLeave: () => {
      if (overIdx.current === index) {
        overIdx.current = null;
        setOver(-1);
      }
    },
    onDrop: (e) => {
      e.preventDefault();
      const from = dragIdx.current;
      const to = index;
      if (from !== null && from !== to) {
        const copy = [...items];
        const [moved] = copy.splice(from, 1);
        copy.splice(to, 0, moved);
        onReorder(copy);
      }
      dragIdx.current = null;
      overIdx.current = null;
      setDragging(-1);
      setOver(-1);
    },
    onDragEnd: () => {
      dragIdx.current = null;
      overIdx.current = null;
      setDragging(-1);
      setOver(-1);
    },
  });

  return { handlers, dragging, over };
}

// ─── Draggable Column Chip ───────────────────────────────────────────────────

function ColumnChip({ name, type, onRemove, dragProps, isDragging, isOver }) {
  return (
    <div
      {...dragProps}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '6px 10px',
        background: isOver ? COLORS.dropTarget : isDragging ? COLORS.dragActive : COLORS.white,
        border: `1px solid ${isOver ? COLORS.primary : COLORS.border}`,
        borderRadius: 20,
        fontSize: 12,
        fontWeight: 500,
        color: COLORS.text,
        cursor: 'grab',
        opacity: isDragging ? 0.5 : 1,
        transition: 'all 0.15s ease',
        transform: isOver ? 'scale(1.03)' : 'scale(1)',
        userSelect: 'none',
      }}
    >
      <DragHandle size={12} />
      <span>{name}</span>
      {type && (
        <span style={{ fontSize: 10, color: COLORS.textLight }}>({type})</span>
      )}
      {onRemove && (
        <button
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: 0,
            marginLeft: 2,
            color: COLORS.red,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <X size={12} />
        </button>
      )}
    </div>
  );
}

// ─── Available Column Chip (draggable into selected) ─────────────────────────

function AvailableColumnChip({ col, onAdd }) {
  return (
    <div
      draggable
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = 'copy';
        e.dataTransfer.setData('application/column', JSON.stringify(col));
      }}
      onClick={() => onAdd(col.column_name)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: '5px 10px',
        background: COLORS.light,
        border: `1px dashed ${COLORS.border}`,
        borderRadius: 20,
        fontSize: 12,
        color: COLORS.textLight,
        cursor: 'pointer',
        userSelect: 'none',
        transition: 'all 0.15s ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = COLORS.primary;
        e.currentTarget.style.color = COLORS.primary;
        e.currentTarget.style.background = COLORS.dropTarget;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = COLORS.border;
        e.currentTarget.style.color = COLORS.textLight;
        e.currentTarget.style.background = COLORS.light;
      }}
    >
      <Plus size={11} />
      <span>{col.column_name}</span>
      <span style={{ fontSize: 10, opacity: 0.7 }}>({col.data_type})</span>
    </div>
  );
}

// ─── Drag-and-Drop Column Picker ─────────────────────────────────────────────

function DragColumnPicker({ columns, selectedColumns, onColumnsChange }) {
  const available = columns.filter((c) => !selectedColumns.includes(c.column_name));
  const { handlers: dragHandlers, dragging, over } = useDragList(selectedColumns, onColumnsChange);

  const handleAdd = (name) => {
    if (!selectedColumns.includes(name)) {
      onColumnsChange([...selectedColumns, name]);
    }
  };

  const handleRemove = (name) => {
    onColumnsChange(selectedColumns.filter((c) => c !== name));
  };

  // Handle drop from available pool
  const handleDropFromAvailable = (e) => {
    e.preventDefault();
    try {
      const colData = e.dataTransfer.getData('application/column');
      if (colData) {
        const col = JSON.parse(colData);
        handleAdd(col.column_name);
      }
    } catch { /* ignore */ }
  };

  const handleSelectAll = () => onColumnsChange(columns.map((c) => c.column_name));
  const handleClearAll = () => onColumnsChange([]);

  return (
    <div style={{
      background: COLORS.white,
      borderRadius: 12,
      padding: 16,
      border: `1px solid ${COLORS.border}`,
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 12,
      }}>
        <label style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          fontSize: 14,
          fontWeight: 600,
          color: COLORS.text,
          margin: 0,
        }}>
          <Columns size={16} color={COLORS.primary} />
          Columns to Display
          {selectedColumns.length > 0 && (
            <span style={{
              fontSize: 11,
              color: COLORS.white,
              background: COLORS.primary,
              borderRadius: 10,
              padding: '2px 8px',
              fontWeight: 600,
            }}>
              {selectedColumns.length}
            </span>
          )}
        </label>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={handleSelectAll}
            style={{
              fontSize: 12,
              color: COLORS.primary,
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              fontWeight: 500,
            }}
          >
            Select All
          </button>
          <button
            onClick={handleClearAll}
            style={{
              fontSize: 12,
              color: COLORS.red,
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              fontWeight: 500,
            }}
          >
            Clear
          </button>
        </div>
      </div>

      {/* Two-panel layout */}
      <div style={{ display: 'flex', gap: 12 }}>
        {/* Available columns */}
        <div style={{
          flex: 1,
          background: COLORS.light,
          borderRadius: 8,
          padding: 12,
          border: `1px solid ${COLORS.border}`,
          minHeight: 100,
        }}>
          <div style={{
            fontSize: 11,
            fontWeight: 600,
            color: COLORS.textLight,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            marginBottom: 8,
          }}>
            Available — click or drag to add
          </div>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 6,
          }}>
            {available.length === 0 ? (
              <span style={{ fontSize: 12, color: COLORS.textLight, fontStyle: 'italic' }}>
                All columns selected
              </span>
            ) : (
              available.map((col) => (
                <AvailableColumnChip key={col.column_name} col={col} onAdd={handleAdd} />
              ))
            )}
          </div>
        </div>

        {/* Arrow indicator */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          flexShrink: 0,
          color: COLORS.textLight,
        }}>
          <ArrowRight size={20} />
        </div>

        {/* Selected columns (drop zone, reorderable) */}
        <div
          onDragOver={(e) => {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'copy';
          }}
          onDrop={handleDropFromAvailable}
          style={{
            flex: 1,
            background: selectedColumns.length === 0 ? `${COLORS.primary}08` : COLORS.white,
            borderRadius: 8,
            padding: 12,
            border: `2px dashed ${selectedColumns.length === 0 ? COLORS.primary : COLORS.border}`,
            minHeight: 100,
            transition: 'all 0.2s ease',
          }}
        >
          <div style={{
            fontSize: 11,
            fontWeight: 600,
            color: COLORS.textLight,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            marginBottom: 8,
          }}>
            Selected — drag to reorder
          </div>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 6,
          }}>
            {selectedColumns.length === 0 ? (
              <span style={{ fontSize: 12, color: COLORS.textLight, fontStyle: 'italic' }}>
                Drop columns here or click from available
              </span>
            ) : (
              selectedColumns.map((colName, idx) => {
                const colDef = columns.find((c) => c.column_name === colName);
                return (
                  <ColumnChip
                    key={colName}
                    name={colName}
                    type={colDef?.data_type}
                    onRemove={() => handleRemove(colName)}
                    dragProps={dragHandlers(idx)}
                    isDragging={dragging === idx}
                    isOver={over === idx}
                  />
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Draggable Filter Row ────────────────────────────────────────────────────

function DraggableFilterRow({ filter, columns, onUpdate, onRemove, dragProps, isDragging, isOver }) {
  return (
    <div
      {...dragProps}
      style={{
        display: 'flex',
        gap: 8,
        alignItems: 'center',
        padding: 12,
        background: isOver ? COLORS.dropTarget : isDragging ? COLORS.dragActive : COLORS.light,
        borderRadius: 8,
        border: `1px solid ${isOver ? COLORS.primary : COLORS.border}`,
        opacity: isDragging ? 0.5 : 1,
        transition: 'all 0.15s ease',
        transform: isOver ? 'translateY(-2px)' : 'none',
      }}
    >
      <DragHandle />

      <select
        value={filter.column}
        onChange={(e) => onUpdate({ ...filter, column: e.target.value })}
        style={{
          padding: '8px 12px',
          borderRadius: 6,
          border: `1px solid ${COLORS.border}`,
          fontSize: 14,
          flex: 1,
          fontFamily: 'inherit',
        }}
      >
        <option value="">Select column</option>
        {columns.map((col) => (
          <option key={col.column_name} value={col.column_name}>
            {col.column_name}
          </option>
        ))}
      </select>

      <select
        value={filter.operator}
        onChange={(e) => onUpdate({ ...filter, operator: e.target.value })}
        style={{
          padding: '8px 12px',
          borderRadius: 6,
          border: `1px solid ${COLORS.border}`,
          fontSize: 14,
          minWidth: 100,
          fontFamily: 'inherit',
        }}
      >
        <option value="">Operator</option>
        {OPERATORS.map((op) => (
          <option key={op} value={op}>{op}</option>
        ))}
      </select>

      {!['IS NULL', 'IS NOT NULL'].includes(filter.operator) && (
        <input
          type="text"
          value={filter.value}
          onChange={(e) => onUpdate({ ...filter, value: e.target.value })}
          placeholder="Value"
          style={{
            padding: '8px 12px',
            borderRadius: 6,
            border: `1px solid ${COLORS.border}`,
            fontSize: 14,
            flex: 1,
          }}
        />
      )}

      <button
        onClick={onRemove}
        style={{
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          padding: 6,
          color: COLORS.red,
        }}
      >
        <Trash2 size={18} />
      </button>
    </div>
  );
}

// ─── Draggable Group-By Chip ─────────────────────────────────────────────────

function GroupByChip({ name, onRemove, dragProps, isDragging, isOver }) {
  return (
    <div
      {...dragProps}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '6px 12px',
        background: isOver ? COLORS.dropTarget : isDragging ? COLORS.dragActive : '#E8F5E2',
        border: `1px solid ${isOver ? COLORS.primary : '#B7E1A1'}`,
        borderRadius: 20,
        fontSize: 12,
        fontWeight: 500,
        color: COLORS.text,
        cursor: 'grab',
        opacity: isDragging ? 0.5 : 1,
        transition: 'all 0.15s ease',
        userSelect: 'none',
      }}
    >
      <DragHandle size={12} />
      <span>{name}</span>
      <button
        onClick={(e) => { e.stopPropagation(); onRemove(); }}
        style={{
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          padding: 0,
          color: COLORS.red,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <X size={12} />
      </button>
    </div>
  );
}

// ─── Draggable Join Row ──────────────────────────────────────────────────────

function DraggableJoinRow({ join, idx, schema, columns, query, onQueryChange, dragProps, isDragging, isOver }) {
  return (
    <div
      {...dragProps}
      style={{
        background: isOver ? COLORS.dropTarget : isDragging ? COLORS.dragActive : COLORS.light,
        padding: 12,
        borderRadius: 8,
        border: `1px solid ${isOver ? COLORS.primary : COLORS.border}`,
        display: 'flex',
        gap: 8,
        alignItems: 'flex-start',
        opacity: isDragging ? 0.5 : 1,
        transition: 'all 0.15s ease',
      }}
    >
      <DragHandle />

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <select
          value={join.table}
          onChange={(e) => {
            const newJoins = [...query.joins];
            newJoins[idx].table = e.target.value;
            onQueryChange({ ...query, joins: newJoins });
          }}
          style={{
            padding: '8px 12px',
            borderRadius: 6,
            border: `1px solid ${COLORS.border}`,
            fontSize: 14,
            fontFamily: 'inherit',
          }}
        >
          <option value="">Join table</option>
          {(schema?.tables || [])
            .filter((t) => t.name !== query.table)
            .map((table) => (
              <option key={table.name} value={table.name}>
                {table.name}
              </option>
            ))}
        </select>

        <div style={{ display: 'flex', gap: 8 }}>
          <select
            value={join.leftColumn}
            onChange={(e) => {
              const newJoins = [...query.joins];
              newJoins[idx].leftColumn = e.target.value;
              onQueryChange({ ...query, joins: newJoins });
            }}
            style={{
              flex: 1,
              padding: '8px 12px',
              borderRadius: 6,
              border: `1px solid ${COLORS.border}`,
              fontSize: 14,
              fontFamily: 'inherit',
            }}
          >
            <option value="">Left column</option>
            {columns.map((col) => (
              <option key={col.column_name} value={col.column_name}>
                {col.column_name}
              </option>
            ))}
          </select>

          <span style={{
            padding: '8px 12px',
            fontSize: 13,
            color: COLORS.textLight,
          }}>
            =
          </span>

          <select
            value={join.rightColumn}
            onChange={(e) => {
              const newJoins = [...query.joins];
              newJoins[idx].rightColumn = e.target.value;
              onQueryChange({ ...query, joins: newJoins });
            }}
            style={{
              flex: 1,
              padding: '8px 12px',
              borderRadius: 6,
              border: `1px solid ${COLORS.border}`,
              fontSize: 14,
              fontFamily: 'inherit',
            }}
          >
            <option value="">Right column</option>
            {(schema?.tables || [])
              .find((t) => t.name === join.table)
              ?.columns.map((col) => (
                <option key={col.column_name} value={col.column_name}>
                  {col.column_name}
                </option>
              ))}
          </select>
        </div>
      </div>

      <button
        onClick={() => {
          const newJoins = query.joins.filter((_, i) => i !== idx);
          onQueryChange({
            ...query,
            joins: newJoins.length > 0 ? newJoins : [],
          });
        }}
        style={{
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          padding: 6,
          color: COLORS.red,
        }}
      >
        <Trash2 size={18} />
      </button>
    </div>
  );
}

// ─── Save Report Modal ──────────────────────────────────────────────────────

function SaveReportModal({ isOpen, onClose, onSave }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const handleSave = () => {
    if (!name.trim()) {
      alert('Please enter a report name');
      return;
    }
    onSave(name, description);
    setName('');
    setDescription('');
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    }}>
      <div style={{
        background: COLORS.white,
        borderRadius: 12,
        padding: 24,
        maxWidth: 400,
        width: '90%',
        boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 20,
        }}>
          <h3 style={{ margin: 0, color: COLORS.text, fontSize: 18, fontWeight: 600 }}>
            Save Report
          </h3>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: COLORS.textLight,
            }}
          >
            <X size={20} />
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label style={{
              display: 'block',
              marginBottom: 6,
              fontSize: 14,
              fontWeight: 500,
              color: COLORS.text,
            }}>
              Report Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Monthly Sales Summary"
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: 6,
                border: `1px solid ${COLORS.border}`,
                fontSize: 14,
                fontFamily: 'inherit',
                boxSizing: 'border-box',
              }}
            />
          </div>

          <div>
            <label style={{
              display: 'block',
              marginBottom: 6,
              fontSize: 14,
              fontWeight: 500,
              color: COLORS.text,
            }}>
              Description (optional)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Add notes about this report"
              rows={3}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: 6,
                border: `1px solid ${COLORS.border}`,
                fontSize: 14,
                fontFamily: 'inherit',
                boxSizing: 'border-box',
                resize: 'vertical',
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
            <button
              onClick={onClose}
              style={{
                padding: '10px 20px',
                borderRadius: 6,
                border: `1px solid ${COLORS.border}`,
                background: COLORS.white,
                color: COLORS.text,
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: 500,
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              style={{
                padding: '10px 20px',
                borderRadius: 6,
                border: 'none',
                background: COLORS.green,
                color: COLORS.white,
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: 500,
              }}
            >
              Save Report
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Saved Reports Panel ────────────────────────────────────────────────────

function SavedReportsPanel({ reports, onLoad, onDelete }) {
  return (
    <div style={{
      width: 280,
      background: COLORS.light,
      borderRight: `1px solid ${COLORS.border}`,
      padding: 16,
      overflowY: 'auto',
      display: 'flex',
      flexDirection: 'column',
      gap: 12,
    }}>
      <h3 style={{
        margin: 0,
        padding: 0,
        fontSize: 16,
        fontWeight: 600,
        color: COLORS.text,
      }}>
        Saved Reports
      </h3>

      {reports.length === 0 ? (
        <p style={{
          margin: 0,
          fontSize: 13,
          color: COLORS.textLight,
          fontStyle: 'italic',
          padding: 12,
        }}>
          No saved reports yet
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {reports.map((report) => (
            <div
              key={report.id}
              style={{
                background: COLORS.white,
                border: `1px solid ${COLORS.border}`,
                borderRadius: 8,
                padding: 12,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: 13,
                  fontWeight: 500,
                  color: COLORS.text,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}>
                  {report.name}
                </div>
                {report.description && (
                  <div style={{
                    fontSize: 12,
                    color: COLORS.textLight,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}>
                    {report.description}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  onClick={() => onLoad(report)}
                  title="Load report"
                  style={{
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 4,
                    color: COLORS.primary,
                  }}
                >
                  <Eye size={16} />
                </button>
                <button
                  onClick={() => onDelete(report.id)}
                  title="Delete report"
                  style={{
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 4,
                    color: COLORS.red,
                  }}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Query Builder Section (with drag-and-drop) ────────────────────────────

function QueryBuilderSection({ schema, query, onQueryChange }) {
  const tables = schema?.tables || [];
  const selectedTable = tables.find((t) => t.name === query.table);
  const columns = selectedTable?.columns || [];

  // Drag reorder for filters
  const filterReorder = useDragList(query.filters, (newFilters) => {
    onQueryChange({ ...query, filters: newFilters });
  });

  // Drag reorder for groupBy
  const groupByReorder = useDragList(query.groupBy, (newGroupBy) => {
    onQueryChange({ ...query, groupBy: newGroupBy });
  });

  // Drag reorder for joins
  const joinReorder = useDragList(query.joins, (newJoins) => {
    onQueryChange({ ...query, joins: newJoins });
  });

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: 20,
    }}>
      {/* Table Selector */}
      <div style={{
        background: COLORS.white,
        borderRadius: 12,
        padding: 16,
        border: `1px solid ${COLORS.border}`,
      }}>
        <label style={{
          display: 'block',
          marginBottom: 8,
          fontSize: 14,
          fontWeight: 600,
          color: COLORS.text,
        }}>
          Table
        </label>
        <select
          value={query.table}
          onChange={(e) => onQueryChange({
            ...query,
            table: e.target.value,
            columns: [],
            groupBy: [],
            orderBy: { column: '', direction: 'ASC' },
          })}
          style={{
            width: '100%',
            padding: '10px 12px',
            borderRadius: 6,
            border: `1px solid ${COLORS.border}`,
            fontSize: 14,
            fontFamily: 'inherit',
          }}
        >
          <option value="">Select a table</option>
          {(schema?.tables || []).map((table) => (
            <option key={table.name} value={table.name}>
              {table.name}
            </option>
          ))}
        </select>
      </div>

      {query.table && (
        <>
          {/* Drag-and-Drop Column Picker */}
          <DragColumnPicker
            columns={columns}
            selectedColumns={query.columns}
            onColumnsChange={(newCols) => onQueryChange({ ...query, columns: newCols })}
          />

          {/* Draggable Filters */}
          <div style={{
            background: COLORS.white,
            borderRadius: 12,
            padding: 16,
            border: `1px solid ${COLORS.border}`,
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 12,
            }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 14,
                fontWeight: 600,
                color: COLORS.text,
                margin: 0,
              }}>
                <Filter size={16} color={COLORS.orange} />
                Filters
                {query.filters.length > 0 && (
                  <span style={{
                    fontSize: 11,
                    color: COLORS.white,
                    background: COLORS.orange,
                    borderRadius: 10,
                    padding: '2px 8px',
                    fontWeight: 600,
                  }}>
                    {query.filters.length}
                  </span>
                )}
              </label>
              <button
                onClick={() => {
                  onQueryChange({
                    ...query,
                    filters: [
                      ...query.filters,
                      { column: '', operator: '', value: '' },
                    ],
                  });
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '6px 12px',
                  borderRadius: 6,
                  border: `1px solid ${COLORS.primary}`,
                  background: 'transparent',
                  color: COLORS.primary,
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: 500,
                }}
              >
                <Plus size={16} /> Add Filter
              </button>
            </div>

            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}>
              {query.filters.length === 0 ? (
                <p style={{
                  margin: 0,
                  fontSize: 13,
                  color: COLORS.textLight,
                  fontStyle: 'italic',
                  padding: 8,
                }}>
                  No filters applied — click "Add Filter" to start
                </p>
              ) : (
                query.filters.map((filter, idx) => (
                  <DraggableFilterRow
                    key={idx}
                    filter={filter}
                    columns={columns}
                    dragProps={filterReorder.handlers(idx)}
                    isDragging={filterReorder.dragging === idx}
                    isOver={filterReorder.over === idx}
                    onUpdate={(updatedFilter) => {
                      const newFilters = [...query.filters];
                      newFilters[idx] = updatedFilter;
                      onQueryChange({ ...query, filters: newFilters });
                    }}
                    onRemove={() => {
                      const newFilters = query.filters.filter((_, i) => i !== idx);
                      onQueryChange({ ...query, filters: newFilters });
                    }}
                  />
                ))
              )}
            </div>
          </div>

          {/* Draggable Group By */}
          <div style={{
            background: COLORS.white,
            borderRadius: 12,
            padding: 16,
            border: `1px solid ${COLORS.border}`,
          }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginBottom: 12,
              fontSize: 14,
              fontWeight: 600,
              color: COLORS.text,
            }}>
              Group By
              {query.groupBy.length > 0 && (
                <span style={{
                  fontSize: 11,
                  color: COLORS.white,
                  background: COLORS.green,
                  borderRadius: 10,
                  padding: '2px 8px',
                  fontWeight: 600,
                }}>
                  {query.groupBy.length}
                </span>
              )}
            </label>

            {/* Selected group-by chips (draggable to reorder) */}
            {query.groupBy.length > 0 && (
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 6,
                marginBottom: 12,
                padding: 10,
                background: '#F0FFF0',
                borderRadius: 8,
                border: '1px dashed #B7E1A1',
                minHeight: 36,
              }}>
                {query.groupBy.map((colName, idx) => (
                  <GroupByChip
                    key={colName}
                    name={colName}
                    dragProps={groupByReorder.handlers(idx)}
                    isDragging={groupByReorder.dragging === idx}
                    isOver={groupByReorder.over === idx}
                    onRemove={() => {
                      onQueryChange({
                        ...query,
                        groupBy: query.groupBy.filter((c) => c !== colName),
                      });
                    }}
                  />
                ))}
              </div>
            )}

            {/* Available columns to add */}
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 6,
            }}>
              {columns
                .filter((col) => !query.groupBy.includes(col.column_name))
                .map((col) => (
                  <button
                    key={col.column_name}
                    onClick={() => {
                      onQueryChange({
                        ...query,
                        groupBy: [...query.groupBy, col.column_name],
                      });
                    }}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      padding: '4px 10px',
                      background: COLORS.light,
                      border: `1px dashed ${COLORS.border}`,
                      borderRadius: 20,
                      fontSize: 12,
                      color: COLORS.textLight,
                      cursor: 'pointer',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = COLORS.green;
                      e.currentTarget.style.color = COLORS.green;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = COLORS.border;
                      e.currentTarget.style.color = COLORS.textLight;
                    }}
                  >
                    <Plus size={11} /> {col.column_name}
                  </button>
                ))}
            </div>
          </div>

          {/* Order By */}
          <div style={{
            background: COLORS.white,
            borderRadius: 12,
            padding: 16,
            border: `1px solid ${COLORS.border}`,
            display: 'flex',
            gap: 12,
          }}>
            <div style={{ flex: 1 }}>
              <label style={{
                display: 'block',
                marginBottom: 8,
                fontSize: 14,
                fontWeight: 600,
                color: COLORS.text,
              }}>
                Order By Column
              </label>
              <select
                value={query.orderBy.column}
                onChange={(e) => onQueryChange({
                  ...query,
                  orderBy: { ...query.orderBy, column: e.target.value },
                })}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: 6,
                  border: `1px solid ${COLORS.border}`,
                  fontSize: 14,
                  fontFamily: 'inherit',
                }}
              >
                <option value="">None</option>
                {columns.map((col) => (
                  <option key={col.column_name} value={col.column_name}>
                    {col.column_name}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ flex: 0.5 }}>
              <label style={{
                display: 'block',
                marginBottom: 8,
                fontSize: 14,
                fontWeight: 600,
                color: COLORS.text,
              }}>
                Direction
              </label>
              <select
                value={query.orderBy.direction}
                onChange={(e) => onQueryChange({
                  ...query,
                  orderBy: { ...query.orderBy, direction: e.target.value },
                })}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: 6,
                  border: `1px solid ${COLORS.border}`,
                  fontSize: 14,
                  fontFamily: 'inherit',
                }}
              >
                <option value="ASC">ASC</option>
                <option value="DESC">DESC</option>
              </select>
            </div>
          </div>

          {/* Limit */}
          <div style={{
            background: COLORS.white,
            borderRadius: 12,
            padding: 16,
            border: `1px solid ${COLORS.border}`,
          }}>
            <label style={{
              display: 'block',
              marginBottom: 8,
              fontSize: 14,
              fontWeight: 600,
              color: COLORS.text,
            }}>
              Result Limit (max 10,000)
            </label>
            <input
              type="number"
              value={query.limit}
              onChange={(e) => {
                let value = parseInt(e.target.value, 10) || 500;
                value = Math.min(Math.max(value, 1), 10000);
                onQueryChange({ ...query, limit: value });
              }}
              min={1}
              max={10000}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: 6,
                border: `1px solid ${COLORS.border}`,
                fontSize: 14,
              }}
            />
          </div>

          {/* Draggable Joins */}
          <div style={{
            background: COLORS.white,
            borderRadius: 12,
            padding: 16,
            border: `1px solid ${COLORS.border}`,
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 12,
            }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 14,
                fontWeight: 600,
                color: COLORS.text,
                margin: 0,
              }}>
                <LinkIcon size={16} color={COLORS.primary} />
                JOINs
                {query.joins.length > 0 && (
                  <span style={{
                    fontSize: 11,
                    color: COLORS.white,
                    background: COLORS.primary,
                    borderRadius: 10,
                    padding: '2px 8px',
                    fontWeight: 600,
                  }}>
                    {query.joins.length}
                  </span>
                )}
              </label>
              <button
                onClick={() => {
                  onQueryChange({
                    ...query,
                    joins: [...query.joins, { table: '', leftColumn: '', rightColumn: '' }],
                  });
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '6px 12px',
                  borderRadius: 6,
                  border: `1px solid ${COLORS.primary}`,
                  background: 'transparent',
                  color: COLORS.primary,
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: 500,
                }}
              >
                <Plus size={16} /> Add JOIN
              </button>
            </div>

            {query.joins.length === 0 ? (
              <p style={{
                margin: 0,
                fontSize: 13,
                color: COLORS.textLight,
                fontStyle: 'italic',
                padding: 8,
              }}>
                No joins configured — click "Add JOIN" to link tables
              </p>
            ) : (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
              }}>
                {query.joins.map((join, idx) => (
                  <DraggableJoinRow
                    key={idx}
                    join={join}
                    idx={idx}
                    schema={schema}
                    columns={columns}
                    query={query}
                    onQueryChange={onQueryChange}
                    dragProps={joinReorder.handlers(idx)}
                    isDragging={joinReorder.dragging === idx}
                    isOver={joinReorder.over === idx}
                  />
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Results Table ──────────────────────────────────────────────────────────

function ResultsTable({ results, isLoading, executionTime }) {
  if (!results) {
    return (
      <div style={{
        background: COLORS.white,
        borderRadius: 12,
        padding: 32,
        border: `1px solid ${COLORS.border}`,
        textAlign: 'center',
      }}>
        <p style={{
          margin: 0,
          fontSize: 14,
          color: COLORS.textLight,
          fontStyle: 'italic',
        }}>
          Run a report to see results
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div style={{
        background: COLORS.white,
        borderRadius: 12,
        padding: 32,
        border: `1px solid ${COLORS.border}`,
        textAlign: 'center',
      }}>
        <p style={{
          margin: 0,
          fontSize: 14,
          color: COLORS.text,
        }}>
          Loading results...
        </p>
      </div>
    );
  }

  const rows = results.rows || [];
  const rowCount = results.rowCount || 0;
  const resultColumns = rows.length > 0 ? Object.keys(rows[0]) : [];

  if (rows.length === 0) {
    return (
      <div style={{
        background: COLORS.white,
        borderRadius: 12,
        padding: 32,
        border: `1px solid ${COLORS.border}`,
        textAlign: 'center',
      }}>
        <p style={{
          margin: 0,
          fontSize: 14,
          color: COLORS.textLight,
          fontStyle: 'italic',
        }}>
          No results found
        </p>
      </div>
    );
  }

  return (
    <div style={{
      background: COLORS.white,
      borderRadius: 12,
      border: `1px solid ${COLORS.border}`,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
    }}>
      <div style={{
        padding: '12px 16px',
        borderBottom: `1px solid ${COLORS.border}`,
        background: COLORS.light,
        fontSize: 13,
        color: COLORS.textLight,
        fontWeight: 500,
      }}>
        {rowCount} row{rowCount !== 1 ? 's' : ''} &bull; Executed in {executionTime}ms
      </div>

      <div style={{
        overflowX: 'auto',
        maxHeight: 500,
        overflowY: 'auto',
      }}>
        <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: 13,
        }}>
          <thead style={{
            position: 'sticky',
            top: 0,
            background: COLORS.light,
            borderBottom: `1px solid ${COLORS.border}`,
          }}>
            <tr>
              {resultColumns.map((col) => (
                <th
                  key={col}
                  style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    fontWeight: 600,
                    color: COLORS.text,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr
                key={idx}
                style={{
                  background: idx % 2 === 0 ? COLORS.white : COLORS.light,
                  borderBottom: `1px solid ${COLORS.border}`,
                }}
              >
                {resultColumns.map((col) => (
                  <td
                    key={`${idx}-${col}`}
                    style={{
                      padding: '12px 16px',
                      color: COLORS.text,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: 300,
                    }}
                    title={String(row[col])}
                  >
                    {row[col] !== null && row[col] !== undefined
                      ? String(row[col])
                      : '(null)'}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Main Component ─────────────────────────────────────────────────────────

export default function ReportBuilderPage({ user, onNavigate }) {
  // Easy Reports (Carlos builder) is the DEFAULT view; the original visual
  // builder lives behind the Advanced toggle, unchanged.
  const [builderMode, setBuilderMode] = useState('easy');
  const [schema, setSchema] = useState(null);
  const [savedReports, setSavedReports] = useState([]);
  const [query, setQuery] = useState({
    table: '',
    columns: [],
    filters: [],
    groupBy: [],
    orderBy: { column: '', direction: 'ASC' },
    limit: 500,
    joins: [],
  });
  const [results, setResults] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [executionTime, setExecutionTime] = useState(0);
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [message, setMessage] = useState(null);

  // Load schema and saved reports on mount
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        if (window.zignagenixAPI?.getReportSchema) {
          const raw = await window.zignagenixAPI.getReportSchema();
          // API returns { schema: { tableName: [columns] }, timestamp }
          // Transform to { tables: [{ name, columns }] } for the UI
          const schemaObj = raw?.schema || raw || {};
          const tables = Object.entries(schemaObj)
            .filter(([, cols]) => Array.isArray(cols))
            .map(([name, columns]) => ({ name, columns }))
            .sort((a, b) => a.name.localeCompare(b.name));
          setSchema({ tables });
        }
        if (window.zignagenixAPI?.getSavedReports) {
          const reports = await window.zignagenixAPI.getSavedReports();
          setSavedReports(reports || []);
        }
      } catch (err) {
        console.error('Failed to load report schema:', err);
        setMessage({
          type: 'error',
          text: 'Failed to load report schema. Please check the API.',
        });
      }
    };

    loadInitialData();
  }, []);

  const runReport = useCallback(async () => {
    if (!query.table) {
      setMessage({ type: 'error', text: 'Please select a table' });
      return;
    }

    if (query.columns.length === 0) {
      setMessage({ type: 'error', text: 'Please select at least one column' });
      return;
    }

    setIsLoading(true);
    setMessage(null);

    try {
      const config = {
        table: query.table,
        columns: query.columns,
        filters: query.filters.filter((f) => f.column && f.operator),
        groupBy: query.groupBy.length > 0 ? query.groupBy : undefined,
        orderBy: query.orderBy.column
          ? { column: query.orderBy.column, direction: query.orderBy.direction }
          : undefined,
        limit: query.limit,
        joins: query.joins.length > 0 ? query.joins : undefined,
      };

      const reportResults = await window.zignagenixAPI.runReport(config);
      setResults(reportResults);
      setExecutionTime(reportResults.executionMs || 0);
      setMessage({ type: 'success', text: 'Report executed successfully' });
    } catch (err) {
      console.error('Failed to run report:', err);
      setMessage({ type: 'error', text: `Failed to run report: ${err.message}` });
    } finally {
      setIsLoading(false);
    }
  }, [query]);

  const exportCsv = useCallback(async () => {
    if (!results) {
      setMessage({ type: 'error', text: 'No results to export' });
      return;
    }

    try {
      const config = {
        table: query.table,
        columns: query.columns,
        filters: query.filters.filter((f) => f.column && f.operator),
        groupBy: query.groupBy.length > 0 ? query.groupBy : undefined,
        orderBy: query.orderBy.column
          ? { column: query.orderBy.column, direction: query.orderBy.direction }
          : undefined,
        limit: query.limit,
        joins: query.joins.length > 0 ? query.joins : undefined,
      };

      const exported = await window.zignagenixAPI.exportReportCsv(config);

      // Create a blob and trigger download
      const blob = new Blob([exported.csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `report-${Date.now()}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);

      setMessage({ type: 'success', text: 'Report exported successfully' });
    } catch (err) {
      console.error('Failed to export report:', err);
      setMessage({ type: 'error', text: `Failed to export report: ${err.message}` });
    }
  }, [query, results]);

  const handleSaveReport = useCallback(async (name, description) => {
    try {
      const config = {
        table: query.table,
        columns: query.columns,
        filters: query.filters.filter((f) => f.column && f.operator),
        groupBy: query.groupBy.length > 0 ? query.groupBy : undefined,
        orderBy: query.orderBy.column
          ? { column: query.orderBy.column, direction: query.orderBy.direction }
          : undefined,
        limit: query.limit,
        joins: query.joins.length > 0 ? query.joins : undefined,
      };

      const response = await window.zignagenixAPI.createSavedReport({
        name,
        description,
        config,
      });

      if (response.success) {
        setSaveModalOpen(false);
        setMessage({ type: 'success', text: `Report "${name}" saved successfully` });

        // Refresh saved reports
        const reports = await window.zignagenixAPI.getSavedReports();
        setSavedReports(reports || []);
      }
    } catch (err) {
      console.error('Failed to save report:', err);
      setMessage({ type: 'error', text: `Failed to save report: ${err.message}` });
    }
  }, [query]);

  const handleLoadReport = useCallback(async (report) => {
    try {
      const fullReport = await window.zignagenixAPI.getSavedReport(report.id);
      const config = fullReport.config || report.config;

      setQuery({
        table: config.table || '',
        columns: config.columns || [],
        filters: config.filters || [],
        groupBy: config.groupBy || [],
        orderBy: config.orderBy || { column: '', direction: 'ASC' },
        limit: config.limit || 500,
        joins: config.joins || [],
      });

      setMessage({ type: 'success', text: `Loaded report: ${report.name}` });
      setResults(null);
    } catch (err) {
      console.error('Failed to load report:', err);
      setMessage({ type: 'error', text: `Failed to load report: ${err.message}` });
    }
  }, []);

  const handleDeleteReport = useCallback(async (reportId) => {
    if (!window.confirm('Are you sure you want to delete this report?')) {
      return;
    }

    try {
      await window.zignagenixAPI.deleteSavedReport(reportId);
      setMessage({ type: 'success', text: 'Report deleted successfully' });

      // Refresh saved reports
      const reports = await window.zignagenixAPI.getSavedReports();
      setSavedReports(reports || []);
    } catch (err) {
      console.error('Failed to delete report:', err);
      setMessage({ type: 'error', text: `Failed to delete report: ${err.message}` });
    }
  }, []);

  if (!schema) {
    return (
      <div style={{
        padding: 32,
        textAlign: 'center',
      }}>
        <p style={{ fontSize: 16, color: COLORS.textLight }}>
          Loading report schema...
        </p>
      </div>
    );
  }

  if (builderMode === 'easy') {
    return (
      <div style={{ padding: 20, background: COLORS.light, minHeight: '100vh' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontSize: 20, color: '#212529' }}>Reports</h2>
          <span style={{ fontSize: 12, color: '#ADB5BD' }}>Pick a report, set a date range, run.</span>
        </div>
        <EasyReports onSwitchToAdvanced={() => setBuilderMode('advanced')} user={user} onNavigate={onNavigate} />
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100vh',
      background: COLORS.light,
    }}>
      {/* Header */}
      <div style={{
        background: COLORS.white,
        borderBottom: `1px solid ${COLORS.border}`,
        padding: '16px 24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}>
          <div style={{
            width: 40,
            height: 40,
            borderRadius: 8,
            background: `${COLORS.primary}18`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <BarChart3 size={24} color={COLORS.primary} />
          </div>
          <div>
            <h1 style={{
              margin: 0,
              fontSize: 24,
              fontWeight: 700,
              color: COLORS.text,
            }}>
              Report Builder
            </h1>
            <p style={{
              margin: 0,
              fontSize: 13,
              color: COLORS.textLight,
            }}>
              Drag and drop to build custom data reports
            </p>
          </div>
        </div>

        <div style={{
          display: 'flex',
          gap: 8,
          alignItems: 'center',
        }}>
          <span style={{
            fontSize: 12,
            fontWeight: 600,
            color: COLORS.text,
            padding: '6px 12px',
            background: COLORS.light,
            borderRadius: 6,
          }}>
            {savedReports.length} saved
          </span>
        </div>
      </div>

      {/* Message Alert */}
      {message && (
        <div style={{
          padding: '12px 24px',
          background: message.type === 'error' ? '#fef2f2' : '#dcfce7',
          borderBottom: `1px solid ${
            message.type === 'error' ? '#fbcfe8' : '#bbf7d0'
          }`,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}>
          {message.type === 'error' ? (
            <AlertCircle size={18} color="#DC3545" />
          ) : (
            <CheckCircle size={18} color="#62BB46" />
          )}
          <span style={{
            fontSize: 13,
            color: message.type === 'error' ? '#DC3545' : '#15803d',
          }}>
            {message.text}
          </span>
        </div>
      )}

      {/* Main Content */}
      <div style={{
        display: 'flex',
        flex: 1,
        overflow: 'hidden',
      }}>
        {/* Saved Reports Sidebar */}
        <SavedReportsPanel
          reports={savedReports}
          onLoad={handleLoadReport}
          onDelete={handleDeleteReport}
        />

        {/* Query Builder + Results */}
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          padding: 24,
          gap: 24,
          overflowY: 'auto',
        }}>
          {/* Query Builder */}
          <QueryBuilderSection
            schema={schema}
            query={query}
            onQueryChange={setQuery}
          />

          {/* Action Buttons */}
          <div style={{
            display: 'flex',
            gap: 12,
            flexWrap: 'wrap',
          }}>
            <button
              onClick={runReport}
              disabled={isLoading}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '12px 20px',
                borderRadius: 6,
                border: 'none',
                background: COLORS.primary,
                color: COLORS.white,
                cursor: isLoading ? 'not-allowed' : 'pointer',
                fontSize: 14,
                fontWeight: 600,
                opacity: isLoading ? 0.7 : 1,
              }}
            >
              <Play size={16} /> Run Report
            </button>

            <button
              onClick={() => setSaveModalOpen(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '12px 20px',
                borderRadius: 6,
                border: `1px solid ${COLORS.border}`,
                background: COLORS.white,
                color: COLORS.text,
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: 600,
              }}
            >
              <Save size={16} /> Save Report
            </button>

            <button
              onClick={exportCsv}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '12px 20px',
                borderRadius: 6,
                border: `1px solid ${COLORS.border}`,
                background: COLORS.white,
                color: COLORS.text,
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: 600,
              }}
            >
              <Download size={16} /> Export CSV
            </button>
          </div>

          {/* Results Table */}
          <ResultsTable
            results={results}
            isLoading={isLoading}
            executionTime={executionTime}
          />
        </div>
      </div>

      {/* Save Report Modal */}
      <SaveReportModal
        isOpen={saveModalOpen}
        onClose={() => setSaveModalOpen(false)}
        onSave={handleSaveReport}
      />
    </div>
  );
}
